import { useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

// Create a custom event for theme changes
const THEME_CHANGE_EVENT = 'themeChange';

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem('theme') as Theme;
    if (storedTheme) {
      return storedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Listen for theme change events
  useEffect(() => {
    const handleThemeChange = (e: CustomEvent<Theme>) => {
      setTheme(e.detail);
    };

    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    };
  }, []);

  // Apply theme changes to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const updateTheme = (newTheme: Theme) => {
    // Update localStorage
    localStorage.setItem('theme', newTheme);
    
    // Dispatch theme change event
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: newTheme }));
    
    // Update document classes
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);
    
    // Update state
    setTheme(newTheme);
  };

  return {
    theme,
    setTheme: updateTheme,
  };
}
